import { VOLLMACHT_DOKUMENTE, VOLLMACHT_STANDARD_KEY, vollmachtAuswahlLabel, vollmachtOrdner } from "@kmvs/forms-core";

/** Eine gewählte Vollmacht in der Kontaktanlage (vor dem Speichern). */
export type VollmachtAuswahl = {
  /** Stabiler Schlüssel für React/Listen — kein Server-Wert. */
  uid: string;
  /** Key aus `VOLLMACHT_DOKUMENTE`; leer, solange das Dropdown noch nichts hat. */
  art: string;
  /** Bereits unterschriebene Fassung — wird nach dem Anlegen hochgeladen. */
  datei?: File | null;
  /** Nach dem Anlegen einen Signierlink erzeugen. */
  signierenNachAnlage?: boolean;
};

/** Status einer Vollmacht am Server (Plan §2.5). */
export type VollmachtStatus = "offen" | "generiert" | "versendet" | "hochgeladen" | "unterschrieben";

/** Eine Vollmacht, wie `GET contact/:id/vollmachten` sie liefert. */
export type VollmachtDto = {
  id: number;
  art: string;
  titel: string;
  status: VollmachtStatus;
  generatedDocumentId: number | null;
  uploadedDocumentId: number | null;
  signedDocumentId: number | null;
  versendetAt: string | null;
  hochgeladenAt: string | null;
  unterschriebenAt: string | null;
  link: { url: string; expiresAt: string; firstOpenedAt: string | null; abgelaufen: boolean } | null;
  fehler?: string;
};

/** Was die Vorschau vor dem Speichern über den Kontakt weiß. */
export type VollmachtVorschauDaten = {
  anzeigeName: string;
  anschrift?: string;
  firma?: string;
  /** Bestehender Kontakt: der Server nimmt Name und Anschrift aus der Akte. */
  contactId?: number;
};

export const STANDARD_VOLLMACHT_UID = "standard";

/** Startwert jeder Anlage: die Kundenerstinformation ist vorausgewählt. */
export const standardVollmachtAuswahl = (): VollmachtAuswahl[] => [
  { uid: STANDARD_VOLLMACHT_UID, art: VOLLMACHT_STANDARD_KEY },
];

export const VOLLMACHT_OPTIONEN: { value: string; label: string }[] = VOLLMACHT_DOKUMENTE.map((d) => ({
  value: d.key,
  label: vollmachtAuswahlLabel(d),
}));

export const vollmachtLabel = (art: string): string =>
  VOLLMACHT_OPTIONEN.find((o) => o.value === art)?.label ?? art;

/** Ablageordner in der Akte, z. B. `Vollmachten/Maklervollmacht` (wie der Server). */
export const vollmachtAblageOrdner = (art: string): string => {
  const d = VOLLMACHT_DOKUMENTE.find((x) => x.key === art);
  return d ? vollmachtOrdner(d) : "Vollmachten";
};

/**
 * Optionen des Dropdowns einer Zeile: Katalogreihenfolge, ohne die Arten, die
 * andere Zeilen schon gewählt haben (je Kontakt höchstens einmal) — die eigene
 * Wahl bleibt sichtbar.
 */
export function freieOptionen(auswahl: VollmachtAuswahl[], eigeneUid: string) {
  const belegt = new Set(auswahl.filter((a) => a.uid !== eigeneUid && a.art).map((a) => a.art));
  return VOLLMACHT_OPTIONEN.filter((o) => !belegt.has(o.value));
}

/** Gewählte Arten ohne leere Zeilen und Dubletten, in Katalogreihenfolge. */
export function gewaehlteArten(auswahl: VollmachtAuswahl[]): string[] {
  const set = new Set(auswahl.map((a) => a.art).filter(Boolean));
  return VOLLMACHT_OPTIONEN.map((o) => o.value).filter((k) => set.has(k));
}

let zaehler = 0;
export const neueVollmachtUid = (): string => `v${Date.now().toString(36)}${(zaehler++).toString(36)}`;

/**
 * Dateiname beim Herunterladen: „Kundenerstinformation – Quip GmbH.pdf".
 * Mit dem Kontaktnamen, damit ein heruntergeladenes Exemplar nicht wie die
 * leere Vorlage aussieht; Zeichen, die Windows im Dateinamen verbietet, fallen weg.
 */
export function vollmachtDateiname(art: string, kontaktName?: string | null, zusatz = ""): string {
  const teile = [vollmachtLabel(art) || "Vollmacht", (kontaktName ?? "").trim()].filter(Boolean);
  return (
    `${teile.join(" – ")}${zusatz}`
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() + ".pdf"
  );
}
